/**
 * Local API client — talks to the in-process leaderboard endpoints served
 * by `server.mjs`. The endpoints are stored in two flavours:
 *
 *   - JSON file (default, ephemeral)
 *   - Postgres (when DATABASE_URL is set on the server)
 *
 * Provides full offline fallback and automatic synchronization with Railway DB.
 */
import {
  mergeWithOfflineScores,
  syncPendingScores,
} from "./offlineStore";

export interface ScoreRow {
  id?: number;
  email?: string;
  display_name: string;
  score: number;
  wave: number;
  completed?: boolean;
  killed_at?: string;
}

let _configured: boolean | null = null;
let _lastProbeTime = 0;

/** Probe /api/healthz and return true if server backend is online & reachable. */
export async function isApiConfigured(): Promise<boolean> {
  const now = Date.now();
  // Cache positive result, but re-probe negative result every 5 seconds
  if (_configured === true) return true;
  if (_configured === false && now - _lastProbeTime < 5000) return false;

  _lastProbeTime = now;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch("/api/healthz", { signal: ctrl.signal, cache: "no-cache" });
    clearTimeout(t);
    if (!res.ok) {
      _configured = false;
      return false;
    }
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      _configured = false;
      return false;
    }
    _configured = true;
  } catch {
    _configured = false;
  }
  return _configured;
}

/** Submit a single score to the backend. Highscore reporting disabled — contest finished. */
export async function submitScore(_row: ScoreRow): Promise<{ offline?: boolean }> {
  // Highscore reporting disabled — contest is finished.
  return Promise.resolve({ offline: false });
}

/** Submit batch scores for remote sync. Highscore reporting disabled — contest finished. */
export async function submitBatchScores(_rows: ScoreRow[]): Promise<{ ok: boolean; synced: number }> {
  return Promise.resolve({ ok: true, synced: 0 });
}

/** Fetch top scores from server, merging local offline scores if present. */
export async function fetchTopScores(limit = 20): Promise<ScoreRow[]> {
  try {
    const res = await fetch(`/api/scores?limit=${limit}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`Leaderboard fetch failed: ${res.status}`);
    const data = await res.json();
    if (!Array.isArray(data)) return mergeWithOfflineScores([]).slice(0, limit);
    _configured = true;
    return mergeWithOfflineScores(data as ScoreRow[]).slice(0, limit);
  } catch {
    _configured = false;
    // Fall back to offline scores
    return mergeWithOfflineScores([]).slice(0, limit);
  }
}

/** Look up the most-recent display name used for a given email. */
export async function fetchDisplayNameForEmail(email: string): Promise<string | null> {
  if (!email) return null;
  try {
    const res = await fetch(
      `/api/scores/lookup?email=${encodeURIComponent(email)}`,
      { cache: "no-store" }
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { display_name?: string | null };
    return data.display_name ?? null;
  } catch {
    return null;
  }
}

/** Trigger sync of all pending offline score entries with the remote server. */
export async function syncOfflineScores(): Promise<{ success: boolean; count: number; error?: string }> {
  return syncPendingScores(submitBatchScores);
}